/**
 * The orbit camera inside a networked dungeon run (AD-706,
 * `q-dungeon-run-camera-occlusion`).
 *
 * Slice 2e (AD-705) draws the run roofless at the instance frame and keeps the
 * game's ordinary orbit camera, whose boom (default 9 m at pitch 0.8) is longer
 * than most chambers are wide: on one filmed seed a 5.5 m wall stood between
 * the lens and the avatar. The fix REUSES the explorer's pull-in
 * (`explorerCamera.ts boomDistance`) rather than forking it, against the
 * same 2.5D footprints the walker predicts on (the run's `buildLayoutCollision`
 * shapes, AD-026/AD-044), given a height: the top of the plan's tallest wall.
 *
 * Unlike the explorer, the run does NOT clamp pitch: with the wall top known, a
 * boom steep enough to clear the walls keeps its full length, and a low boom is
 * pulled in short of the first wall behind the avatar. The player's zoom
 * (`OrbitCamera.dist`) is never changed — the result is a per-frame render
 * distance that can only shorten it. Pure; frontend-local (AD-122).
 */

import type { CollisionShape } from '@great-cloud/shared/client';

import { boomDistance, DUNGEON_CAMERA } from './explorerCamera.js';

/** Exactly what the occluder top reads from a plan (a schema-v2 `DungeonPlan` satisfies it). */
export interface RunWallHeights {
  layout?: { chambers: readonly { wallHeight: number }[] };
}

/**
 * The top of the plan's tallest wall. A corridor's walls are the lower of its
 * two chambers' (`planLayoutWalls`), so the chambers bound every wall. A plan
 * with no layout or no usable height returns `Infinity` — every footprint then occludes at
 * any height, the explorer's conservative rule.
 */
export function runOccluderTop(plan: RunWallHeights): number {
  let top = 0;
  for (const chamber of plan.layout?.chambers ?? []) {
    if (Number.isFinite(chamber.wallHeight) && chamber.wallHeight > top) top = chamber.wallHeight;
  }
  return top > 0 ? top : Infinity;
}

/**
 * The render distance for the orbit boom this frame: the player's own `dist`
 * when nothing blocks the view, shorter when a wall below `occluderTop` stands
 * between the avatar and the lens. `target` is the avatar's FEET (the
 * `orbitPose` contract), in the instance frame the shapes are in.
 */
/**
 * The lift (AD-706, found by the lane's first run): pulled in alone, a wall
 * right behind the avatar collapses the boom to the explorer's 0.9 m floor,
 * which at the game's pitch puts the lens BELOW the look point — a worm's-eye
 * close-up. The run is roofless, so the better shot is over the wall: the
 * render pitch rises (never the player's own) until the boom keeps a usable
 * length. Every value here is presentation tuning, not a bound.
 */
export const RUN_CAMERA = {
  /** A boom at least this long (or the player's own, if shorter) needs no lift. */
  USABLE_DIST_M: 3,
  /** Pitch search step. Coarse on purpose: the lift is eased, so the step never shows. */
  PITCH_STEP: 0.1,
  /** Ease rate of the render pitch, per second (≈ 95% settled in half a second). */
  PITCH_EASE_PER_S: 6,
} as const;

/**
 * The lowest pitch, from the player's own up to `maxPitch`, at which the
 * pulled-in boom still keeps `min(dist, USABLE_DIST_M)`; `maxPitch` when none
 * does. Never lower than the player's pitch.
 */
export function runLiftPitch(
  target: { x: number; z: number },
  orbit: { yaw: number; pitch: number; dist: number },
  shapes: readonly CollisionShape[],
  occluderTop: number,
  maxPitch: number,
): number {
  const usable = Math.min(orbit.dist, RUN_CAMERA.USABLE_DIST_M);
  for (let pitch = orbit.pitch; pitch < maxPitch; pitch += RUN_CAMERA.PITCH_STEP) {
    if (runBoomDistance(target, { ...orbit, pitch }, shapes, occluderTop) >= usable) return pitch;
  }
  return Math.max(orbit.pitch, maxPitch);
}

/** Ease the render pitch toward `wanted` over `dtMs` (frame-rate independent). */
export function easeRunPitch(current: number, wanted: number, dtMs: number): number {
  const k = 1 - Math.exp((-RUN_CAMERA.PITCH_EASE_PER_S * Math.max(0, dtMs)) / 1000);
  return current + (wanted - current) * k;
}

export function runBoomDistance(
  target: { x: number; z: number },
  orbit: { yaw: number; pitch: number; dist: number },
  shapes: readonly CollisionShape[],
  occluderTop: number,
): number {
  return boomDistance(
    target.x,
    target.z,
    orbit.yaw,
    orbit.pitch,
    orbit.dist,
    shapes,
    DUNGEON_CAMERA.PROBE_RADIUS_M,
    DUNGEON_CAMERA.PROBE_STEP_M,
    occluderTop,
  );
}
